import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plug2, ShieldCheck } from 'lucide-react';
import { Toggle } from '@/components/ui/Toggle';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import ActivationConfirmationModal from '@/components/ui/ActivationConfirmationModal';
import { useLtiConfig, useSetLtiEnabled } from '@/hooks/useLti';

/** Switch LTI on or off for this tenant. */
export default function LtiIntegrationSetting({ canWrite = false }: { canWrite?: boolean }) {
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';

  const { data: config, isLoading } = useLtiConfig();
  const setEnabled = useSetLtiEnabled();
  const [confirmDisable, setConfirmDisable] = useState(false);

  // A local form purely so the shared Toggle can be reused — it is
  // react-hook-form bound, and this setting has no form of its own.
  const { control, setValue } = useForm<{ enabled: boolean }>({
    defaultValues: { enabled: false },
  });

  useEffect(() => {
    if (config) setValue('enabled', config.enabled);
  }, [config, setValue]);

  const handleChange = (checked: boolean) => {
    if (!checked) {
      // Switching off stops every launch from every registered LMS, so it asks.
      setValue('enabled', true); // hold the switch until the choice is made
      setConfirmDisable(true);
      return;
    }
    setEnabled.mutate(true);
  };

  return (
    <div className="card">
      <div className="flex items-start gap-3 mb-4">
        <Plug2 className="w-5 h-5 text-brand-indigo-600 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">LTI Integration</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {`Let customer LMS platforms (Moodle, Canvas, …) launch ${companyName} courses, certifications and learning paths.`}
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <Toggle
            name="enabled"
            control={control}
            label="Enable LTI integration"
            description={
              config?.enabled
                ? `Registered LMS platforms can launch ${companyName} content.`
                : 'Launches from any LMS are rejected while this is off.'
            }
            disabled={!canWrite || setEnabled.isPending}
            onChange={handleChange}
          />

          {config?.enabled && config?.has_master_key && (
            <p className="flex items-center gap-2 text-xs text-green-700 mt-3">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              Signing keys are protected by this tenant's encryption key.
            </p>
          )}
        </>
      )}

      <ActivationConfirmationModal
        isOpen={confirmDisable}
        onClose={() => {
          setConfirmDisable(false);
          setValue('enabled', true);
        }}
        onConfirm={() =>
          setEnabled.mutate(false, {
            onSettled: () => setConfirmDisable(false),
            onSuccess: () => setValue('enabled', false),
          })
        }
        title="Turn off LTI integration?"
        description={
          'Launches from every registered LMS will be rejected — learners part-way through their ' +
          'content will not be able to reopen it, and grades will stop being sent back. Your ' +
          'platform registrations and keys are kept, so switching it back on restores everything.'
        }
        isActivating={false}
        isLoading={setEnabled.isPending}
      />
    </div>
  );
}
